<template>
  <div>
    <div v-if="loading" class="loading-templates">
      <i class="ri-loader-4-line spin"></i>
      <span>載入 Wixtar 票券中...</span>
    </div>
    <div v-else class="template-selector">
      <div class="template-search">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋票券名稱、活動或票券 ID..."
          @focus="$event.target.select()"
        />
      </div>
      <div class="template-list">
        <div
          v-for="ticket in filteredTickets"
          :key="ticket.ticketId"
          class="template-item"
          :class="{ selected: isSelected(ticket.ticketId) }"
          @click="toggle(ticket.ticketId)"
        >
          <input
            type="checkbox"
            :checked="isSelected(ticket.ticketId)"
            @change="toggle(ticket.ticketId)"
            @click.stop
          />
          <div class="template-info">
            <div class="template-name">{{ ticket.ticketName }}</div>
            <div class="template-details">
              <span v-if="ticket.activitySubject" class="template-type">{{ ticket.activitySubject }}</span>
              <span v-if="ticket.availableQuantity != null" class="template-discount">
                剩餘 {{ ticket.availableQuantity }}
              </span>
              <span v-if="ticket.status === 'sold_out'" class="template-validity">已發完</span>
            </div>
          </div>
        </div>
        <div v-if="filteredTickets.length === 0" class="no-templates">
          <i class="ri-inbox-line"></i>
          <p>沒有找到 Wixtar 票券</p>
          <small v-if="searchQuery">請嘗試其他搜尋關鍵字</small>
        </div>
      </div>
    </div>
    <p v-if="exhaustedSelected.length" class="reward-stock-warning">
      {{ exhaustedSelected.map((ticket) => ticket.ticketName || ticket.ticketId).join('、') }} 目前已發完，發送時會失敗
    </p>
    <div v-if="selectedTickets.length > 0" class="selected-templates">
      <div class="selected-label">已選取 {{ selectedTickets.length }} 張 Wixtar 票券：</div>
      <div class="selected-tags">
        <span
          v-for="ticket in selectedTickets"
          :key="ticket.ticketId"
          class="selected-tag"
        >
          {{ ticket.ticketName }}
          <button type="button" class="remove-tag" @click="remove(ticket.ticketId)">
            <i class="ri-close-line"></i>
          </button>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { isWixtarTicketExhausted } from '@/utils/rewardStockWarning.js';

export default {
  name: 'WixtarTicketMultiSelect',
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    tickets: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const searchQuery = ref('');

    const filteredTickets = computed(() => {
      const query = searchQuery.value.trim().toLowerCase();
      if (!query) return props.tickets;
      return props.tickets.filter((ticket) => {
        const name = (ticket.ticketName || '').toLowerCase();
        const activity = (ticket.activitySubject || '').toLowerCase();
        const id = (ticket.ticketId || '').toLowerCase();
        return name.includes(query) || activity.includes(query) || id.includes(query);
      });
    });

    const selectedTickets = computed(() => {
      return (props.modelValue || []).map((ticketId) => {
        const id = String(ticketId);
        const found = props.tickets.find((ticket) => ticket.ticketId === id);
        return found || { ticketId: id, ticketName: id };
      });
    });

    const exhaustedSelected = computed(() => selectedTickets.value.filter(isWixtarTicketExhausted));

    const isSelected = (ticketId) => {
      return (props.modelValue || []).map(String).includes(String(ticketId));
    };

    const emitIds = (ids) => {
      emit('update:modelValue', ids);
    };

    const toggle = (ticketId) => {
      const id = String(ticketId);
      const current = (props.modelValue || []).map(String);
      if (current.includes(id)) {
        emitIds(current.filter((item) => item !== id));
      } else {
        emitIds([...current, id]);
      }
    };

    const remove = (ticketId) => {
      const id = String(ticketId);
      emitIds((props.modelValue || []).map(String).filter((item) => item !== id));
    };

    return {
      searchQuery,
      filteredTickets,
      selectedTickets,
      exhaustedSelected,
      isSelected,
      toggle,
      remove
    };
  }
};
</script>

<style scoped>
.loading-templates {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 20px;
  color: #666;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.template-selector {
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
}

.template-search {
  padding: 12px;
  border-bottom: 1px solid #eee;
}

.template-search input {
  width: 100%;
}

.template-list {
  max-height: 300px;
  overflow-y: auto;
  padding: 8px;
}

.template-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.template-item:hover {
  background: #f5f5f5;
  border-color: #1976d2;
}

.template-item.selected {
  background: #e3f2fd;
  border-color: #1976d2;
}

.template-item input[type="checkbox"] {
  margin-top: 2px;
  cursor: pointer;
}

.template-info {
  flex: 1;
}

.template-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.template-details {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 12px;
  color: #666;
}

.template-type {
  padding: 2px 8px;
  background: #f0f0f0;
  border-radius: 12px;
}

.template-discount {
  color: #1976d2;
  font-weight: 500;
}

.template-validity {
  color: #666;
}

.reward-stock-warning {
  color: #b45309;
  font-size: 13px;
  margin: 8px 0 0;
}

.no-templates {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.no-templates i {
  font-size: 48px;
  margin-bottom: 12px;
  display: block;
}

.selected-templates {
  margin-top: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 4px;
}

.selected-label {
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
}

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.selected-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #1976d2;
  color: white;
  border-radius: 16px;
  font-size: 14px;
}

.remove-tag {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  font-size: 16px;
  line-height: 1;
}

.remove-tag:hover {
  opacity: 0.8;
}
</style>
